import { Alert, Autocomplete, Button, TextField } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useGoldGamesBrands, useGoldGamesGames } from '../../queries/goldGames';
import type { CashbackBonusFormValues } from '../../schema';
import type { GoldGamesBrand, GoldGamesGame } from '../../@types/goldGames';
import CashbackTierFields, { type CashbackTierProps } from './CashbackTierFields';

type Props = CashbackTierProps & {
  marketplaceId: string;
  currency: string;
};

const CashbackGoldGamesTierRow = ({ marketplaceId, currency, ...props }: Props) => {
  const { control, setValue } = useFormContext<CashbackBonusFormValues>();
  const tier = useWatch({ control, name: `freeSpinTiers.${props.index}` });
  const brands = useGoldGamesBrands(marketplaceId);
  const games = useGoldGamesGames(marketplaceId, tier.providerId, currency);
  const game = games.data?.games.find((item) => String(item.id) === tier.gameId);

  const clearBetSettings = () => {
    setValue(`freeSpinTiers.${props.index}.freeSpinsAwarded`, null, { shouldDirty: true, shouldValidate: true });
    setValue(`freeSpinTiers.${props.index}.betValuePerSpin`, null, { shouldDirty: true, shouldValidate: true });
    setValue(
      `freeSpinTiers.${props.index}.goldGames`,
      {
        betLevel: 0,
        coinValue: 0,
        validityHours: tier.goldGames?.validityHours ?? 24,
      },
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const selectGame = (selected: GoldGamesGame | null) => {
    setValue(`freeSpinTiers.${props.index}.gameId`, selected ? String(selected.id) : '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(`freeSpinTiers.${props.index}.gameName`, selected?.name ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    clearBetSettings();
  };

  const selectProvider = (selected: GoldGamesBrand | null) => {
    setValue(`freeSpinTiers.${props.index}.providerId`, selected ? String(selected.id) : '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(`freeSpinTiers.${props.index}.providerName`, selected?.name ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    selectGame(null);
  };

  const catalogUnavailable = brands.isLoading || brands.isError || games.isError;
  const bettingDisabled = props.disabled || catalogUnavailable || games.isFetching || !game;

  return (
    <>
      {(brands.isError || games.isError) && (
        <Alert
          severity='error'
          action={
            <Button
              onClick={() => {
                void brands.refetch();
                if (tier.providerId) void games.refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Unable to load Gold Games providers or games.
        </Alert>
      )}
      <CashbackTierFields {...props} disabled={props.disabled || catalogUnavailable}>
        <Controller
          control={control}
          name={`freeSpinTiers.${props.index}.providerId`}
          render={({ field, fieldState }) => (
            <Autocomplete
              options={brands.data?.brands ?? []}
              value={brands.data?.brands.find((brand) => String(brand.id) === field.value) ?? null}
              onChange={(_event, selected) => selectProvider(selected)}
              onBlur={field.onBlur}
              getOptionLabel={(brand) => brand.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={props.disabled || catalogUnavailable}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Provider'
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name={`freeSpinTiers.${props.index}.gameId`}
          render={({ field, fieldState }) => (
            <Autocomplete
              options={games.data?.games ?? []}
              value={games.data?.games.find((item) => String(item.id) === field.value) ?? null}
              onChange={(_event, selected) => selectGame(selected)}
              onBlur={field.onBlur}
              getOptionLabel={(item) => item.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={props.disabled || catalogUnavailable || games.isFetching || !tier.providerId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Game'
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name={`freeSpinTiers.${props.index}.betValuePerSpin`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label={`Bet Value per Spin (${currency})`}
              type='number'
              required
              disabled={bettingDisabled}
              onChange={(event) => field.onChange(event.target.value === '' ? null : Number(event.target.value))}
              error={!!fieldState.error}
              helperText={
                fieldState.error?.message ??
                'Configured value for cashback accounting; must match the actual provider bet. Up to 2 decimal places.'
              }
              slotProps={{ htmlInput: { min: 0, step: '0.01', inputMode: 'decimal' } }}
            />
          )}
        />
        <Controller
          control={control}
          name={`freeSpinTiers.${props.index}.goldGames.betLevel`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value === undefined || Number.isNaN(field.value) ? '' : field.value}
              label='Bet Level'
              type='number'
              required
              disabled={bettingDisabled}
              onChange={(event) => field.onChange(event.target.value === '' ? NaN : Number(event.target.value))}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
            />
          )}
        />
        <Controller
          control={control}
          name={`freeSpinTiers.${props.index}.goldGames.coinValue`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value || ''}
              label='Coin Value'
              type='number'
              required
              disabled={bettingDisabled}
              onChange={(event) => field.onChange(Number(event.target.value))}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{ htmlInput: { min: 0, step: 'any', inputMode: 'decimal' } }}
            />
          )}
        />
        <Controller
          control={control}
          name={`freeSpinTiers.${props.index}.goldGames.validityHours`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value ?? 24}
              type='number'
              required
              label='Spin validity (hours after settlement)'
              disabled={bettingDisabled}
              onChange={(event) => field.onChange(Number(event.target.value))}
              error={!!fieldState.error}
              helperText={
                fieldState.error?.message ??
                "1–720 hours. A new cashback award replaces this player's previous cashback spins for this game."
              }
            />
          )}
        />
      </CashbackTierFields>
    </>
  );
};

export default CashbackGoldGamesTierRow;
